import React, { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { PageHeader, KpiCard, PrimaryButton, StatusBadge } from "@/components/ui-parts";
import { RefreshCw, Loader2, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) {
      toast.error("Konnte Übersicht nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data: start } = await api.post("/reconcile", {});
      if (start.status !== "running" || !start.run_id) {
        toast.success(`Abgleich abgeschlossen · ${start.matched || 0} neue Zuordnungen`);
        load();
        return;
      }
      const runId = start.run_id;
      let attempts = 0;
      while (attempts < 240) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: job } = await api.get(`/reconcile/${runId}`);
          if (job.status === "done") {
            const parts = [`${job.matched || 0} neue Zuordnungen`];
            if (job.errors) parts.push(`${job.errors} Fehler`);
            toast.success(`Abgleich beendet · ${parts.join(" · ")}`);
            load();
            return;
          }
        } catch (_) { /* transient — keep polling */ }
      }
      toast.warning("Abgleich läuft weiter im Hintergrund — Seite neu laden für Ergebnis");
    } catch {
      toast.error("Abgleich fehlgeschlagen");
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Übersicht"
        subtitle="Ihr Buchhaltungs-Cockpit – Belege, Auszüge und offene Zuordnungen im Blick."
        actions={
          <PrimaryButton onClick={runReconcile} disabled={reconciling} data-testid="reconcile-btn">
            {reconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" strokeWidth={1.75} />}
            Abgleich jetzt starten
          </PrimaryButton>
        }
      />

      <div className="px-8 py-8 space-y-8">
        {loading || !data ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
              <KpiCard label="Firmen" value={data.totals.companies} testid="kpi-companies" sublabel="Aktive Firmen im Konto" />
              <KpiCard label="Belege" value={data.totals.receipts} testid="kpi-receipts"
                sublabel={`davon ${data.totals.receipts_unmatched} nicht zugeordnet`}
                tone={data.totals.receipts_unmatched > 0 ? "warning" : "success"}
              />
              <KpiCard label="Buchungen" value={data.totals.transactions} testid="kpi-transactions"
                sublabel={`${data.totals.transactions_unmatched} ohne Beleg`}
                tone={data.totals.transactions_unmatched > 0 ? "warning" : "success"}
              />
              <KpiCard label="Kontoauszüge" value={data.totals.statements} testid="kpi-statements" sublabel="verarbeitet" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white border border-[#E6E6E4] rounded-md">
                <div className="border-b border-[#E6E6E4] px-6 py-4 flex items-center justify-between">
                  <div>
                    <div className="font-display font-bold text-base text-[#1A1A1A]">Firmen-Statistik</div>
                    <div className="text-xs text-[#5C5E60] mt-0.5">Belege und Buchungen je Firma</div>
                  </div>
                  <Link to="/app/firmen" className="text-xs font-semibold text-[#18322B] hover:underline">
                    Verwalten →
                  </Link>
                </div>
                {data.per_company.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#5C5E60]">
                    Noch keine Firmen angelegt.{" "}
                    <Link to="/app/firmen" className="font-semibold text-[#18322B] hover:underline">Jetzt anlegen →</Link>
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="companies-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8]">
                        <th className="text-left px-6 py-2.5 font-semibold">Firma</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Belege</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Offen</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Buchungen</th>
                        <th className="text-right px-6 py-2.5 font-semibold">Offen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_company.map((c) => (
                        <tr key={c.id} className="border-t border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150">
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                              <span className="font-medium text-[#1A1A1A]">{c.name}</span>
                            </div>
                          </td>
                          <td className="text-right px-4 py-3 font-mono-tab">{c.receipts}</td>
                          <td className="text-right px-4 py-3 font-mono-tab">
                            {c.receipts_unmatched > 0 ? (
                              <StatusBadge variant="warning">{c.receipts_unmatched}</StatusBadge>
                            ) : (
                              <span className="text-[#5C5E60]">0</span>
                            )}
                          </td>
                          <td className="text-right px-4 py-3 font-mono-tab">{c.transactions}</td>
                          <td className="text-right px-6 py-3 font-mono-tab">
                            {c.transactions_unmatched > 0 ? (
                              <StatusBadge variant="warning">{c.transactions_unmatched}</StatusBadge>
                            ) : (
                              <span className="text-[#5C5E60]">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white border border-[#E6E6E4] rounded-md">
                <div className="border-b border-[#E6E6E4] px-6 py-4">
                  <div className="font-display font-bold text-base text-[#1A1A1A]">Automatischer Abgleich</div>
                  <div className="text-xs text-[#5C5E60] mt-0.5">Läuft alle 2 Tage</div>
                </div>
                <div className="p-6 space-y-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Letzter Abgleich</div>
                    <div className="mt-1 text-[#1A1A1A]">{fmtDateTime(data.last_reconciliation)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Nächster Abgleich</div>
                    <div className="mt-1 text-[#1A1A1A]">{fmtDateTime(data.next_auto_reconcile)}</div>
                  </div>
                  {(data.totals.receipts_unmatched > 0 || data.totals.transactions_unmatched > 0) && (
                    <div className="rounded-md bg-[#FFF4E5] text-[#915000] text-xs px-3 py-2 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} />
                      <div>
                        Es gibt offene Zuordnungen. Prüfen Sie die{" "}
                        <Link to="/app/zuordnung" className="font-semibold underline">Zuordnung</Link>.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
